import { useMemo } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { useData, useDrivers } from "../store/dataStore";
import { useDrill } from "../store/drillStore";
import { runDrill } from "../query/runDrill";
import { arrBridge, nrr } from "../query/metrics";
import { fmtMoney, fmtPct } from "../query/format";
import { KpiCard } from "../components/KpiCard";
import { Drillable } from "../components/Drillable";
import { DriverCell } from "../components/DriverCell";

const TYPES = ["new", "expansion", "contraction", "churn"];
const TYPE_LABEL = { new: "New", expansion: "Expansion", contraction: "Contraction", churn: "Churn" };

const EMPTY = [];

function computeBridge(movements, openingArr) {
  const bridge = arrBridge(openingArr, movements);
    const months = [...new Set(movements.map((m) => m.month))].sort();
    let running = openingArr;
    const monthRows = months.map((month) => {
      const byType = Object.fromEntries(TYPES.map((t) => [t, movements.filter((m) => m.month === month && m.type === t).reduce((s, m) => s + m.amount, 0)]));
      const net = TYPES.reduce((s, t) => s + byType[t], 0);
      running += net;
      return { month, ...byType, net, ending: running };
    });
    const steps = [
      { name: "Opening", value: openingArr, type: null },
      { name: "New", value: bridge.newArr, type: "new" },
      { name: "Expansion", value: bridge.expansion, type: "expansion" },
      { name: "Contraction", value: bridge.contraction, type: "contraction" },
      { name: "Churn", value: bridge.churn, type: "churn" },
      { name: "Ending", value: bridge.endingARR, type: null },
    ];
    let cum = 0;
    const waterfall = steps.map((s, i) => {
      if (i === 0 || i === steps.length - 1) return { ...s, base: 0, size: s.value };
      const start = openingArr + cum;
      cum += s.value;
      const end = openingArr + cum;
      return { ...s, base: Math.min(start, end), size: Math.abs(s.value) };
    });
    return { bridge, months, monthRows, waterfall };
}

export default function ArrBridge() {
  const movements = useData((s) => s.tables.fact_arr_movement) ?? EMPTY;
  const { openingArr, setDriver } = useDrivers();
  const openAt = useDrill((s) => s.openAt);
  const { bridge, monthRows, waterfall } = useMemo(() => computeBridge(movements, openingArr), [movements, openingArr]);

  const netNew = bridge.newArr + bridge.expansion + bridge.contraction + bridge.churn;
  const churnPct = openingArr ? (Math.abs(bridge.churn + bridge.contraction) / openingArr) * 100 : 0;
  const expPct = openingArr ? (bridge.expansion / openingArr) * 100 : 0;
  const nrrV = nrr(churnPct, expPct);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-black tracking-tight" style={{ color: "var(--navy)" }}>ARR Bridge</h2>
          <p className="text-sm mt-1" style={{ color: "var(--slate)" }}>
            Every figure rolls up from {movements.length.toLocaleString()} atomic ARR movement events. Opening ARR is a{" "}
            <DriverCell testId="driver-opening-arr" value={openingArr} display={fmtMoney(openingArr)} onSave={(v) => setDriver("openingArr", v)} /> driver.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        <KpiCard testId="kpi-opening-arr" label="Opening ARR" value={fmtMoney(openingArr)} sub="Driver assumption" />
        <KpiCard testId="kpi-net-new-arr" label="Net New ARR" value={fmtMoney(netNew)} sub={`${movements.length} movement events`} status={netNew > 0 ? "pass" : "miss"} module="arr" seed={[]} />
        <KpiCard testId="kpi-ending-arr" label="Ending ARR" value={fmtMoney(bridge.endingARR)} sub={`${fmtPct((netNew / openingArr) * 100, 1)} growth`} progress={bridge.endingARR / (openingArr * 1.35)} module="arr" seed={[]} />
        <KpiCard testId="kpi-nrr" label="NRR (period)" value={fmtPct(nrrV, 1)} status={nrrV >= 110 ? "pass" : nrrV >= 100 ? "watch" : "miss"} module="arr" seed={[{ field: "type", value: "expansion" }]} />
      </div>

      <div className="panel p-5">
        <div className="text-[11px] uppercase tracking-widest font-bold mb-3" style={{ color: "var(--slate)" }}>ARR Waterfall — click any bar to drill into its movement events</div>
        <div style={{ height: 280 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={waterfall} margin={{ top: 10, right: 10, bottom: 0, left: 10 }}>
              <XAxis dataKey="name" tick={{ fontSize: 12, fill: "var(--slate)" }} />
              <YAxis tickFormatter={fmtMoney} tick={{ fontSize: 11, fill: "var(--slate)" }} width={70} />
              <Tooltip content={({ active, payload, label }) => active && payload?.length ? (
                <div className="rounded-md shadow-lg px-3 py-2 text-xs font-mono" style={{ background: "var(--navy)", color: "#fff" }}>
                  <div className="font-bold" style={{ color: "var(--cyan)" }}>{label}</div>
                  <div style={{ color: "var(--teal)" }}>{fmtMoney(payload.find((p) => p.dataKey === "size")?.payload.value)}</div>
                </div>) : null} />
              <Bar dataKey="base" stackId="wf" fill="transparent" />
              <Bar dataKey="size" stackId="wf" radius={[3, 3, 0, 0]} cursor="pointer"
                onClick={(d) => d && openAt("arr", d.type ? [{ field: "type", value: d.type }] : [])}>
                {waterfall.map((s) => (
                  <Cell key={s.name} fill={s.type === null ? "var(--navy2)" : s.value >= 0 ? "var(--teal)" : "var(--bad)"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="panel overflow-x-auto">
        <table className="dtable" data-testid="arr-month-table">
          <thead>
            <tr><th>Month</th>{TYPES.map((t) => <th key={t} className="text-right">{TYPE_LABEL[t]}</th>)}<th className="text-right">Net</th><th className="text-right">Ending ARR</th></tr>
          </thead>
          <tbody>
            {monthRows.map((r) => (
              <tr key={r.month}>
                <td>
                  <Drillable module="arr" seed={[{ field: "month", value: r.month }]} testId={`drill-month-${r.month}`} className="inline-block">
                    <span className="fact-cell font-semibold pr-4">{r.month}</span>
                  </Drillable>
                </td>
                {TYPES.map((t) => (
                  <td key={t} className="text-right">
                    <Drillable module="arr" seed={[{ field: "month", value: r.month }, { field: "type", value: t }]} testId={`drill-${r.month}-${t}`} className="inline-block">
                      <span className="fact-cell pr-4" style={{ color: r[t] < 0 ? "var(--bad)" : "var(--navy)" }}>{fmtMoney(r[t])}</span>
                    </Drillable>
                  </td>
                ))}
                <td className="text-right num font-bold" style={{ color: r.net >= 0 ? "var(--good)" : "var(--bad)" }}>{fmtMoney(r.net)}</td>
                <td className="text-right num" style={{ color: "var(--navy)" }}>{fmtMoney(r.ending)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
